import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./ClientGallery.css";
import publicOrigin from "./publicOrigin";
import FacePhoto, { FaceAvatar } from "./FacePhoto";

// navigator.clipboard is missing outside secure contexts (and in some in-app
// browsers), so fall back to the old textarea + execCommand trick.
async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy failed");
}

// Keep a consistent filename-based order: oldest filename first, latest last.
// Module-scope + pure so it can back a useMemo without being a changing dep.
const sortByFilenameAsc = (list) =>
  [...list].sort((a, b) => {
    const aName = (a?.original_filename || a?.filename || a?.name || a?.image_url || "").toString();
    const bName = (b?.original_filename || b?.filename || b?.name || b?.image_url || "").toString();
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: "base" });
  });

const SWIPE_THRESHOLD = 80;

// One gallery card. Swipe state lives HERE (not in the parent) so a touch-move
// only re-renders this single card instead of the whole gallery + re-sorting the
// entire loaded list every frame. React.memo keeps siblings from re-rendering on
// unrelated parent updates (banner tick, another card's vote, load-more append).
const PhotoCard = React.memo(function PhotoCard({ image, index, filename, voteDisabled, onVote, onOpen }) {
  const [swipe, setSwipe] = useState(null); // { offset, direction } | null
  const touch = useRef(null);

  const onTouchStart = (e) => {
    // Ignore multi-touch (pinch) so it doesn't get misread as a swipe
    if (e.touches.length !== 1) { touch.current = null; return; }
    const t = e.touches[0];
    touch.current = { startX: t.clientX, startY: t.clientY, swiping: false };
  };

  const onTouchMove = (e) => {
    const ref = touch.current;
    if (!ref) return;
    if (e.touches.length !== 1) {
      // A second finger joined mid-gesture — abandon the swipe
      touch.current = null;
      setSwipe(null);
      return;
    }
    const t = e.touches[0];
    const diffX = t.clientX - ref.startX;
    const diffY = t.clientY - ref.startY;
    // Only swipe horizontally if dominant direction
    if (!ref.swiping && Math.abs(diffX) > 10 && Math.abs(diffX) > Math.abs(diffY)) {
      ref.swiping = true;
    }
    if (ref.swiping) {
      e.preventDefault();
      setSwipe({ offset: diffX, direction: diffX > 0 ? "selected" : "unselected" });
    }
  };

  const onTouchEnd = () => {
    const ref = touch.current;
    if (ref && ref.swiping && swipe && Math.abs(swipe.offset) > SWIPE_THRESHOLD) {
      onVote(image.id, swipe.direction);
    }
    setSwipe(null);
    touch.current = null;
  };

  const swipeOffset = swipe ? swipe.offset : 0;
  const swipeDir = swipe ? swipe.direction : null;
  const swipeOpacity = swipe ? Math.min(Math.abs(swipe.offset) / SWIPE_THRESHOLD, 1) : 0;

  return (
    <div className="cg-card">
      <div
        className="cg-image-wrapper"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => { if (!touch.current?.swiping) onOpen(index); }}
        style={{
          transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
          transition: swipeOffset ? 'none' : 'transform 0.3s ease',
        }}
      >
        <img
          src={image.thumbnail_url || image.image_url}
          alt={`Photo ${index + 1}`}
          className="cg-image"
          loading="lazy"
        />
        {/* Swipe overlay hint */}
        {swipeDir && (
          <div
            className={`cg-swipe-overlay ${swipeDir}`}
            style={{ opacity: swipeOpacity }}
          >
            <span className="cg-swipe-label">
              {swipeDir === "selected" ? "✅ Select" : "⬜ Unselect"}
            </span>
          </div>
        )}
        {!swipeDir && (
          <span className={`cg-vote-badge-label ${image.vote || "none"}`}>
            {image.vote === "selected" ? "✅ Selected" : "⬜ Not selected"}
          </span>
        )}
        {image.is_duplicate && (
          <span className="cg-duplicate-indicator">📋 Duplicate</span>
        )}
        <span className="cg-filename-overlay" title={filename}>
          {filename}
        </span>
      </div>
      <div className="cg-card-actions">
        {/* One toggle, not a select/reject pair. Every photo starts
            unselected, so the only action is picking the ones they want
            — nothing has to be actively turned down. */}
        <button
          className={`cg-vote-btn toggle ${image.vote === "selected" ? "active" : ""}`}
          onClick={() => onVote(image.id, image.vote === "selected" ? "unselected" : "selected")}
          disabled={voteDisabled}
        >
          {image.vote === "selected" ? "✅ Selected" : "Select"}
        </button>
      </div>
      <div className="cg-swipe-hint">← Swipe to unselect · select →</div>
    </div>
  );
});

function ClientGallery() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [clientId] = useState(
    () => localStorage.getItem("cg_clientId") || "client_" + Date.now()
  );

  // Face scan state
  const [showFaceScan, setShowFaceScan] = useState(false);
  const [scanImage, setScanImage] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // People / cluster browse state
  const [showPeople, setShowPeople] = useState(false);
  const [peopleClusters, setPeopleClusters] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [personLoading, setPersonLoading] = useState(false);
  const [activeCluster, setActiveCluster] = useState(null);
  // Which person's share sheet is open, and whether its link was just copied
  const [shareCluster, setShareCluster] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Vote filter state
  const [voteFilter, setVoteFilter] = useState("all");

  // Filter panel state
  const [showFilter, setShowFilter] = useState(false);

  // Folder tab state — null means "All"
  const [activeFolder, setActiveFolder] = useState(null);

  // Duplicate filter state
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showDuplicateSection, setShowDuplicateSection] = useState(false);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [rotation, setRotation] = useState(0);
  const [fullView, setFullView] = useState(false);
  const lightboxTouchRef = useRef(null);

  // Lightbox zoom/pan state (mobile pinch-to-zoom + double-tap)
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const imgContainerRef = useRef(null);
  const imgRef = useRef(null);
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, startOffset: { x: 0, y: 0 } });
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

  // Studio branding (logo + name) shown in the header
  const [studioName, setStudioName] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  // Banner slider state
  const [banners, setBanners] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const bannerTimerRef = useRef(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [folderSummary, setFolderSummary] = useState([]); // [{name, count}] for all folders
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectionLimit, setSelectionLimit] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("cg_clientId", clientId);
  }, [clientId]);

  // Mirror of `images` so stable callbacks (handleVote) can read the latest
  // list without depending on it — which would recreate them every render and
  // defeat React.memo on the cards.
  const imagesRef = useRef([]);
  useEffect(() => { imagesRef.current = images; }, [images]);

  // Auto-slide banners every 4 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    bannerTimerRef.current = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(bannerTimerRef.current);
  }, [banners.length]);

  // Paginated image fetch. offset=0 replaces images; offset>0 appends.
  const fetchImages = useCallback(async (folder, offset) => {
    const isFirst = offset === 0;
    if (!isFirst) setLoadingMore(true);
    try {
      const params = new URLSearchParams({ clientId, offset, limit: 50 });
      if (folder) params.set('folder', folder);
      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/images?${params}`);
      const data = await res.json();
      if (res.ok) {
        setImages(prev => isFirst ? data.images : [...prev, ...data.images]);
        setHasMore(data.hasMore ?? false);
        setDuplicateCount(data.duplicateCount || 0);
        if (data.folderSummary?.length) setFolderSummary(data.folderSummary);
      }
    } catch (err) {
      console.error("Failed to load images:", err);
    } finally {
      if (!isFirst) setLoadingMore(false);
    }
  }, [token, clientId]);

  // Load next page — guarded against concurrent calls
  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    fetchImages(activeFolder, images.length);
  }, [hasMore, loadingMore, activeFolder, images.length, fetchImages]);

  // Infinite scroll sentinel — callback ref recreates observer when loadMore changes
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observerRef.current.observe(node);
  }, [loadMore]);

  // On mount: fetch event (gets defaultFolder) then load first page for that folder
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}?clientId=${clientId}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) { setError(data.message || "Invalid or expired link"); setLoading(false); return; }
        setEvent(data.event);
        setStudioName(data.studioName || null);
        setLogoUrl(data.logoUrl || null);
        setBanners(data.banners || []);
        setSelectedCount(data.event?.selectedCount || 0);
        setSelectionLimit(data.event?.selection_limit ?? null);
        const folder = data.event?.defaultFolder || null;
        setActiveFolder(folder);
        await fetchImages(folder, 0);
      } catch (err) {
        if (!cancelled) setError("Failed to load event");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [token, clientId, fetchImages]);

  const handleVote = useCallback(async (imageId, vote) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/shared/${token}/vote`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageId, vote, clientId }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        if (data.limitReached) {
          setError(`Selection limit of ${data.selectionLimit} photos reached`);
          setTimeout(() => setError(""), 3000);
        }
        return;
      }
      const updateList = (list) =>
        list.map((img) =>
          img.id === imageId ? { ...img, vote: data.vote } : img
        );
      // Track selected count delta — read the latest list from the ref so this
      // callback stays stable (no `images` dependency).
      const prevVote = imagesRef.current.find(img => img.id === imageId)?.vote || null;
      if (prevVote !== 'selected' && data.vote === 'selected') setSelectedCount(c => c + 1);
      if (prevVote === 'selected' && data.vote !== 'selected') setSelectedCount(c => Math.max(0, c - 1));
      setImages(updateList);
      setFilteredImages((prev) => (prev ? updateList(prev) : prev));
      setSuccess(vote === "selected" ? "Photo selected ✅" : "Photo unselected");
      setTimeout(() => setSuccess(""), 1500);
    } catch (err) {
      setError("Failed to record vote");
      setTimeout(() => setError(""), 2000);
    }
  }, [token, clientId]);

  // Face scan handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file) => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setScanImage(e.target.result);
      setScanPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFaceSearch = async () => {
    if (!scanImage) {
      setError("Please upload a face photo first");
      return;
    }

    setScanning(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/shared/${token}/search-face`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageData: scanImage,
            threshold: 0.55,
            clientId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Face search failed");
      } else if (data.matches && data.matches.length > 0) {
        setFilteredImages(data.matches);
        setSuccess(`Found ${data.matches.length} photos of you!`);
        setShowFaceScan(false);
      } else {
        setSuccess("No matching photos found. Try a clearer face photo.");
        setFilteredImages([]);
      }
    } catch (err) {
      setError("Face search failed: " + err.message);
    } finally {
      setScanning(false);
    }
  };

  const clearFaceFilter = () => {
    setFilteredImages(null);
    setScanImage(null);
    setScanPreview(null);
    setActiveCluster(null);
  };

  const openPeople = async () => {
    setShowPeople(true);
    setActiveCluster(null);
    if (peopleClusters.length > 0) return; // already fetched
    setPeopleLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/people`);
      const data = await res.json();
      setPeopleClusters(data.clusters || []);
    } catch (e) {
      console.error('People fetch failed:', e);
    }
    setPeopleLoading(false);
  };

  const handleSelectPerson = async (cluster) => {
    setPersonLoading(true);
    setActiveCluster(cluster);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/people/${cluster.id}/photos`);
      const data = await res.json();
      // Use photos directly from the people endpoint (full list, not limited to loaded images).
      // Enrich with vote/duplicate data from whatever images happen to be already loaded.
      const loadedById = Object.fromEntries(images.map(img => [img.id, img]));
      const personPhotos = (data.photos || []).map(p => ({
        ...p,
        vote: loadedById[p.id]?.vote,
        is_duplicate: loadedById[p.id]?.is_duplicate || false,
        folder_name: loadedById[p.id]?.folder_name || 'General',
        original_filename: loadedById[p.id]?.original_filename || `photo-${p.id}.jpg`,
      }));
      setFilteredImages(personPhotos);
    } catch (e) {
      console.error('Person photos fetch failed:', e);
    }
    setPersonLoading(false);
    setShowPeople(false);
    setShareCluster(null);
  };

  const personShareUrl = (cluster) => `${publicOrigin()}/shared/${token}/person/${cluster.id}`;
  const personShareTitle = (cluster) =>
    cluster.label ? `${cluster.photo_count} photos with ${cluster.label}` : `${cluster.photo_count} photos`;

  // Names what the link actually opens, so "share" does not read as "share
  // this thumbnail".
  const personGalleryName = (cluster) =>
    cluster.label ? `${cluster.label}'s gallery` : "This person's gallery";

  const handlePreviewPersonGallery = (cluster) => {
    window.open(personShareUrl(cluster), '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppPerson = (cluster) => {
    const url = personShareUrl(cluster);
    const msg = cluster.label
      ? `Here are ${cluster.label}'s photos (${cluster.photo_count}) — ${url}`
      : `Here are your photos (${cluster.photo_count}) — ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  // The share icon opens a small sheet so "Copy link" is always reachable —
  // the native share sheet doesn't offer it on every platform.
  const handleSharePerson = (cluster, e) => {
    e.stopPropagation();
    setShareCopied(false);
    setShareCluster(cluster);
  };

  const handleCopyPersonLink = async (cluster) => {
    const url = personShareUrl(cluster);
    try {
      await copyText(url);
      setShareCopied(true);
      setSuccess("Link copied to clipboard!");
      setTimeout(() => setSuccess(""), 2000);
      setTimeout(() => { setShareCluster(null); setShareCopied(false); }, 900);
    } catch (err) {
      setError("Could not copy link");
      setTimeout(() => setError(""), 2000);
    }
  };

  const handleNativeSharePerson = async (cluster) => {
    try {
      await navigator.share({ title: personShareTitle(cluster), url: personShareUrl(cluster) });
      setShareCluster(null);
    } catch (err) { /* user cancelled */ }
  };

  // Folder names from server summary — correct counts across all images, not just loaded ones.
  const folderNames = folderSummary.map(f => f.name);

  // Server already filters images by activeFolder; filteredImages (face filter) bypasses that.
  const baseImages = filteredImages !== null ? filteredImages : images;

  // Sorting + filtering the whole loaded list is O(n log n) over hundreds of
  // images. Without memoization it re-ran on every render — every banner tick,
  // every swipe frame, every vote — which is a big part of the scroll jank.
  // Recompute only when the list or an active filter actually changes.
  const sortedBaseImages = useMemo(
    () => sortByFilenameAsc(baseImages),
    [baseImages]
  );
  // After the duplicate filter but before the vote filter — the filter panel
  // uses this to count photos per status.
  const afterDupFilter = useMemo(
    () => hideDuplicates ? sortedBaseImages.filter((img) => !img.is_duplicate) : sortedBaseImages,
    [sortedBaseImages, hideDuplicates]
  );
  const displayImages = useMemo(
    () => voteFilter === "all" ? afterDupFilter : afterDupFilter.filter((img) => img.vote === voteFilter),
    [afterDupFilter, voteFilter]
  );
  const duplicateImages = useMemo(
    () => sortedBaseImages.filter((img) => img.is_duplicate),
    [sortedBaseImages]
  );
  const lightboxImage = lightboxIndex >= 0 ? displayImages[lightboxIndex] : null;

  const resetZoom = () => { setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); };
  // Stable so React.memo'd cards don't re-render when this identity changes.
  const openLightbox = useCallback((index) => {
    setRotation(0); setFullView(false); setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); setLightboxIndex(index);
  }, []);
  const closeLightbox = () => { setRotation(0); setFullView(false); resetZoom(); setLightboxIndex(-1); };
  const goPrev = () => { setRotation(0); resetZoom(); setLightboxIndex((i) => (i > 0 ? i - 1 : displayImages.length - 1)); };
  const goNext = () => { setRotation(0); resetZoom(); setLightboxIndex((i) => (i < displayImages.length - 1 ? i + 1 : 0)); };
  const rotateCW = () => setRotation((r) => (r - 90 + 360) % 360);
  const toggleFullView = () => setFullView((v) => !v);
  const getImageFilename = (image) =>
    image?.original_filename || image?.filename || image?.name || "Untitled";

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Keep panning within the bounds of the zoomed image so it can't be dragged off-screen
  const clampOffset = (offset, scale) => {
    const container = imgContainerRef.current;
    const img = imgRef.current;
    if (!container || !img || scale <= 1) return { x: 0, y: 0 };
    const containerRect = container.getBoundingClientRect();
    const maxX = Math.max(0, (img.offsetWidth * scale - containerRect.width) / 2);
    const maxY = Math.max(0, (img.offsetHeight * scale - containerRect.height) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, offset.x)),
      y: Math.min(maxY, Math.max(-maxY, offset.y)),
    };
  };

  const toggleZoomAt = () => {
    if (zoomScale > 1) {
      resetZoom();
    } else {
      setZoomScale(2.5);
      setZoomOffset({ x: 0, y: 0 });
    }
  };

  // Lightbox gesture handlers for mobile: pinch-to-zoom, pan-when-zoomed,
  // double-tap-to-zoom, and swipe-to-navigate (only while not zoomed in —
  // otherwise a pinch's incidental horizontal drift got misread as a swipe).
  const handleLightboxTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        active: true,
        startDist: getTouchDistance(e.touches),
        startScale: zoomScale,
      };
      panRef.current.active = false;
      lightboxTouchRef.current = null;
      return;
    }

    if (e.touches.length !== 1) return;
    const touch = e.touches[0];

    // Double-tap detection
    const now = Date.now();
    const last = lastTapRef.current;
    if (now - last.time < 300 && Math.abs(touch.clientX - last.x) < 30 && Math.abs(touch.clientY - last.y) < 30) {
      toggleZoomAt();
      lastTapRef.current = { time: 0, x: 0, y: 0 };
      lightboxTouchRef.current = null;
      return;
    }
    lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };

    if (zoomScale > 1) {
      panRef.current = { active: true, startX: touch.clientX, startY: touch.clientY, startOffset: zoomOffset };
      lightboxTouchRef.current = null;
    } else {
      lightboxTouchRef.current = touch.clientX;
    }
  };

  const handleLightboxTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      const scale = Math.min(4, Math.max(1, pinchRef.current.startScale * (dist / pinchRef.current.startDist)));
      setZoomScale(scale);
      setZoomOffset((prev) => clampOffset(prev, scale));
      return;
    }
    if (panRef.current.active && e.touches.length === 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panRef.current.startX;
      const dy = e.touches[0].clientY - panRef.current.startY;
      setZoomOffset(clampOffset(
        { x: panRef.current.startOffset.x + dx, y: panRef.current.startOffset.y + dy },
        zoomScale
      ));
    }
  };

  const handleLightboxTouchEnd = (e) => {
    if (pinchRef.current.active) {
      pinchRef.current.active = false;
      if (zoomScale <= 1.05) resetZoom();
      return;
    }
    if (panRef.current.active) {
      panRef.current.active = false;
      return;
    }
    if (lightboxTouchRef.current === null || zoomScale > 1) {
      lightboxTouchRef.current = null;
      return;
    }
    const diff = e.changedTouches[0].clientX - lightboxTouchRef.current;
    if (diff > 60) goPrev();
    else if (diff < -60) goNext();
    lightboxTouchRef.current = null;
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex < 0) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") closeLightbox();
      else if (e.key === "f" || e.key === "F") toggleFullView();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, displayImages.length]);

  if (loading) {
    return (
      <div className="cg-loading">
        <CameraLoader text="Loading photos..." />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="cg-error-page">
        <h1>😕 Oops!</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="cg-container">
      {/* Header */}
      <header className="cg-header">
        <div className="cg-header-content">
          {(logoUrl || studioName) && (
            <div className="cg-studio-brand">
              {logoUrl && <img src={logoUrl} alt="" className="cg-studio-logo" />}
              {studioName && <span className="cg-studio-name">{studioName}</span>}
            </div>
          )}
          <h1 className="cg-title">{event?.event_name}</h1>
          <p className="cg-subtitle">
            {event?.groom_name} & {event?.bridal_name}
            {event?.event_date &&
              ` • ${new Date(event.event_date).toLocaleDateString()}`}
          </p>
          {event?.face_cluster_count > 0 && (
            <div className="cg-face-stat">
              <span className="cg-face-stat-icon">👥</span>
              <span><strong>{event.face_cluster_count}</strong> unique faces captured in this moment</span>
            </div>
          )}
        </div>
        <div className="cg-header-actions">
          <button
            className="cg-face-scan-btn"
            onClick={() => setShowFaceScan(!showFaceScan)}
          >
            📷 Find My Photos
          </button>
          <button
            className="cg-face-scan-btn"
            onClick={openPeople}
          >
            👥 Browse by Person
          </button>
          {filteredImages !== null && (
            <button className="cg-clear-filter-btn" onClick={clearFaceFilter}>
              ✕ {activeCluster ? `${activeCluster.label || 'Person'}'s photos` : 'Face filter'} — Show All
            </button>
          )}
        </div>
      </header>

      {/* Banner Advertisement Slider */}
      {banners.length > 0 && (
        <div className="cg-banner-slider">
          <div className="cg-banner-track" style={{ transform: `translateX(-${bannerIndex * 100}%)` }}>
            {banners.map((banner, idx) => (
              <div key={banner.id} className="cg-banner-slide">
                <div
                  className="cg-banner-backdrop"
                  style={{ backgroundImage: `url(${banner.image_url})` }}
                  aria-hidden="true"
                />
                <img src={banner.image_url} alt={`Ad ${idx + 1}`} className="cg-banner-img" />
              </div>
            ))}
          </div>
          {banners.length > 1 && (
            <div className="cg-banner-dots">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  className={`cg-banner-dot ${idx === bannerIndex ? 'active' : ''}`}
                  onClick={() => setBannerIndex(idx)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      {error && <div className="cg-error">{error}</div>}
      {success && <div className="cg-success">{success}</div>}

      {/* Face Scan Panel */}
      {showFaceScan && (
        <div className="cg-face-scan-panel">
          <h3>🔍 Scan Your Face to Find Your Photos</h3>
          <p>Upload a selfie or photo of your face to filter and show only photos you appear in.</p>

          <div
            className={`cg-scan-drop-area ${dragActive ? "active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="cg-scan-file-input"
            />
            {scanPreview ? (
              <img src={scanPreview} alt="Scan" className="cg-scan-preview" />
            ) : (
              <div className="cg-scan-placeholder">
                <span>📸</span>
                <p>Drop your photo here or tap to browse</p>
              </div>
            )}
          </div>

          <div className="cg-scan-actions">
            <button
              className="cg-scan-search-btn"
              onClick={handleFaceSearch}
              disabled={!scanImage || scanning}
            >
              {scanning ? "Scanning..." : "🔍 Search"}
            </button>
            <button
              className="cg-scan-cancel-btn"
              onClick={() => setShowFaceScan(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* People / Browse by Person modal */}
      {showPeople && (
        <div className="cg-people-overlay" onClick={() => { setShowPeople(false); setShareCluster(null); }}>
          <div className="cg-people-modal" onClick={e => e.stopPropagation()}>
            <div className="cg-people-header">
              <h3 className="cg-people-title">👥 Browse by Person</h3>
              <button className="cg-people-close" onClick={() => { setShowPeople(false); setShareCluster(null); }}>✕</button>
            </div>

            {peopleLoading || personLoading ? (
              <div className="cg-people-loading">
                <div className="cg-spinner" />
                <p>{personLoading ? 'Loading photos…' : 'Finding people…'}</p>
              </div>
            ) : peopleClusters.length === 0 ? (
              <div className="cg-people-empty">
                <p>👤</p>
                <p>The studio hasn't analyzed faces for this event yet.<br />Try <strong>Find My Photos</strong> to search with a selfie instead.</p>
              </div>
            ) : (
              <>
                <p className="cg-people-hint">Tap a person to see all their photos</p>
                <div className="cg-people-grid">
                  {peopleClusters.map(cluster => (
                    <div key={cluster.id} className="cg-person-card" onClick={() => handleSelectPerson(cluster)}>
                      <div className="cg-person-photo">
                        {cluster.cover_url
                          ? <FacePhoto
                              src={cluster.cover_url}
                              box={cluster.cover_face_box}
                              avatarClassName="cg-person-face-avatar"
                            />
                          : <div className="cg-person-placeholder">👤</div>
                        }
                        <span className="cg-person-count">{cluster.photo_count}</span>
                        <button
                          className="cg-person-share-btn"
                          onClick={(e) => handleSharePerson(cluster, e)}
                          title={`Share ${cluster.label ? `${cluster.label}'s` : "this person's"} gallery page`}
                          aria-label={`Share ${cluster.label ? `${cluster.label}'s` : "this person's"} gallery page`}
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.367A2.52 2.52 0 0 1 13 4.5Z"/></svg>
                        </button>
                      </div>
                      <span className="cg-person-name">{cluster.label || 'Unknown'}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {shareCluster && (
              <div className="cg-share-sheet-overlay" onClick={() => setShareCluster(null)}>
                <div className="cg-share-sheet" onClick={e => e.stopPropagation()}>
                  <div className="cg-share-sheet-header">
                    {shareCluster.cover_url && (
                      <FaceAvatar
                        src={shareCluster.cover_url}
                        box={shareCluster.cover_face_box}
                        className="cg-share-sheet-avatar"
                      />
                    )}
                    <span className="cg-share-sheet-title">
                      {personGalleryName(shareCluster)}
                    </span>
                    <span className="cg-share-sheet-sub">
                      A personal page with the {shareCluster.photo_count}{' '}
                      {shareCluster.photo_count === 1 ? 'photo' : 'photos'} they appear in
                    </span>
                  </div>

                  {/* The person page is reached through this event's share link
                      and links back to the full gallery, so say so rather than
                      implying the link is private to one person. */}
                  <p className="cg-share-note">
                    Anyone with this link can view and download these photos, and can
                    also open the full event gallery.
                  </p>

                  <div className="cg-share-url" title={personShareUrl(shareCluster)}>
                    {personShareUrl(shareCluster)}
                  </div>

                  <button
                    className={`cg-share-option primary ${shareCopied ? 'copied' : ''}`}
                    onClick={() => handleCopyPersonLink(shareCluster)}
                  >
                    {shareCopied ? '✅ Copied!' : '🔗 Copy link'}
                  </button>

                  <button
                    className="cg-share-option"
                    onClick={() => handleWhatsAppPerson(shareCluster)}
                  >
                    💬 Send on WhatsApp
                  </button>

                  {typeof navigator !== 'undefined' && navigator.share && (
                    <button
                      className="cg-share-option"
                      onClick={() => handleNativeSharePerson(shareCluster)}
                    >
                      📤 Share via…
                    </button>
                  )}

                  <button
                    className="cg-share-preview"
                    onClick={() => handlePreviewPersonGallery(shareCluster)}
                  >
                    👁 Preview this gallery
                  </button>

                  <button className="cg-share-cancel" onClick={() => setShareCluster(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Folder Tabs + Filter Icon */}
      <div className="cg-tab-bar">
        <div className="cg-folder-tabs">
          <button
            className={`cg-folder-tab ${!activeFolder ? "active" : ""}`}
            onClick={() => { setActiveFolder(null); setLightboxIndex(-1); setImages([]); fetchImages(null, 0); }}
          >
            All
            <span className="cg-folder-tab-count">{folderSummary.reduce((s, f) => s + f.count, 0)}</span>
          </button>
          {folderNames.map((name) => {
            const count = folderSummary.find(f => f.name === name)?.count || 0;
            return (
              <button
                key={name}
                className={`cg-folder-tab ${activeFolder === name ? "active" : ""}`}
                onClick={() => { setActiveFolder(name); setLightboxIndex(-1); setImages([]); fetchImages(name, 0); }}
              >
                {name}
                <span className="cg-folder-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
        {selectionLimit != null ? (
          <div className="cg-selection-progress">
            <div className="cg-selection-progress-label">
              <span>✓ {selectedCount} / {selectionLimit}</span>
              {selectedCount >= selectionLimit && <span className="cg-selection-limit-hit">Limit reached</span>}
            </div>
            <div className="cg-selection-progress-bar">
              <div
                className="cg-selection-progress-fill"
                style={{ width: `${Math.min(100, (selectedCount / selectionLimit) * 100)}%` }}
              />
            </div>
          </div>
        ) : selectedCount > 0 && (
          <div className="cg-selected-count-pill">✓ {selectedCount}</div>
        )}
        <button
          className={`cg-filter-icon-btn ${voteFilter !== "all" || hideDuplicates ? "has-active" : ""}`}
          onClick={() => setShowFilter(true)}
          aria-label="Filter photos"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.629V2.34a.75.75 0 0 1 .628-.74Z" clipRule="evenodd"/>
          </svg>
          Filter
          {(voteFilter !== "all" || hideDuplicates) && <span className="cg-filter-dot" />}
        </button>
      </div>

      {/* Filter Slide Panel */}
      {showFilter && (
        <div className="cg-filter-overlay" onClick={() => setShowFilter(false)}>
          <div className="cg-filter-panel" onClick={(e) => e.stopPropagation()}>
            <div className="cg-filter-panel-header">
              <span className="cg-filter-panel-title">Filter Photos</span>
              <button className="cg-filter-panel-close" onClick={() => setShowFilter(false)}>✕</button>
            </div>
            <div className="cg-filter-panel-section">
              <p className="cg-filter-panel-label">Status</p>
              <div className="cg-filter-panel-btns">
                {["all", "selected", "unselected"].map((f) => {
                  const count = f === "all" ? afterDupFilter.length : afterDupFilter.filter((img) => img.vote === f).length;
                  return (
                    <button
                      key={f}
                      className={`cg-filter-panel-btn ${f} ${voteFilter === f ? "active" : ""}`}
                      onClick={() => { setVoteFilter(f); setLightboxIndex(-1); }}
                    >
                      {f === "all" ? "📷 All" : f === "selected" ? "✅ Selected" : "⬜ Not selected"}
                      <span className="cg-vote-filter-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {duplicateCount > 0 && (
              <div className="cg-filter-panel-section">
                <p className="cg-filter-panel-label">Duplicate Photos</p>
                <div className="cg-filter-dup-info">
                  <span className="cg-filter-dup-icon">📋</span>
                  <span className="cg-filter-dup-text">{duplicateCount} similar photos found</span>
                </div>
                <button
                  className={`cg-filter-panel-btn duplicate ${hideDuplicates ? "active" : ""}`}
                  onClick={() => { setHideDuplicates(!hideDuplicates); setLightboxIndex(-1); }}
                >
                  {hideDuplicates ? "👁️ Show Duplicates" : "🙈 Hide Duplicates"}
                  <span className="cg-vote-filter-count">{duplicateCount}</span>
                </button>
              </div>
            )}
            <button
              className="cg-filter-panel-done"
              onClick={() => setShowFilter(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Filter Status */}
      {filteredImages !== null && (
        <div className="cg-filter-status">
          Showing {filteredImages.length} photo{filteredImages.length !== 1 ? "s" : ""} matching your face
        </div>
      )}


      {/* Gallery */}
      {displayImages.length === 0 ? (
        <div className="cg-empty">
          <p>📷</p>
          <p>No photos to display</p>
        </div>
      ) : (
        <div className="cg-gallery">
          {displayImages.map((image, index) => (
            <PhotoCard
              key={image.id}
              image={image}
              index={index}
              filename={getImageFilename(image)}
              voteDisabled={selectionLimit != null && selectedCount >= selectionLimit && image.vote !== "selected"}
              onVote={handleVote}
              onOpen={openLightbox}
            />
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel — mounts when server has more pages */}
      {hasMore && !loadingMore && (
        <div ref={sentinelRef} className="cg-load-sentinel" />
      )}
      {loadingMore && (
        <div className="cg-load-sentinel">
          <div className="cg-load-spinner" />
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className={`cg-lightbox ${fullView ? "cg-fullview" : ""}`}
          onClick={closeLightbox}
          onTouchStart={handleLightboxTouchStart}
          onTouchMove={handleLightboxTouchMove}
          onTouchEnd={handleLightboxTouchEnd}
        >
          {/* Prev Arrow */}
          <button
            className="cg-lightbox-arrow cg-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
          >
            ‹
          </button>

          <div className="cg-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="cg-lightbox-close" onClick={closeLightbox}>
              ✕
            </button>
            <div className="cg-lightbox-img-container" ref={imgContainerRef}>
              <img
                ref={imgRef}
                src={lightboxImage.image_url}
                alt="Enlarged"
                className={`cg-lightbox-image ${fullView ? "cg-fullview-image" : ""} ${zoomScale > 1 ? "cg-zoomed" : ""}`}
                onDoubleClick={toggleZoomAt}
                style={{
                  transform: `rotate(${rotation}deg) scale(${zoomScale}) translate(${zoomOffset.x / zoomScale}px, ${zoomOffset.y / zoomScale}px)`,
                  transition: (pinchRef.current.active || panRef.current.active) ? 'none' : 'transform 0.2s ease',
                  ...(rotation ? {
                    maxWidth: rotation % 180 !== 0 ? (fullView ? '95vh' : '65vh') : '100%',
                    maxHeight: rotation % 180 !== 0 ? (fullView ? '95vw' : '85vw') : (fullView ? '95vh' : '65vh'),
                  } : {}),
                }}
              />
            </div>
            <div className={`cg-lightbox-bottom ${fullView ? "cg-fullview-bottom" : ""}`}>
              <div className="cg-lightbox-counter">
                {lightboxIndex + 1} / {displayImages.length}
                <span className="cg-zoom-hint">🤏 Pinch or double-tap to zoom</span>
                {lightboxImage.vote && (
                  <span className={`cg-lightbox-vote-status ${lightboxImage.vote}`}>
                    {lightboxImage.vote === "selected" ? "✅ Selected" : "⬜ Not selected"}
                  </span>
                )}
              </div>
              <div className="cg-lightbox-actions">
                <button
                  className={`cg-fullview-btn ${fullView ? "active" : ""}`}
                  onClick={toggleFullView}
                  title="Full View (F)"
                >
                  {fullView ? "⛶ Exit Full" : "⛶ Full View"}
                </button>
                <button
                  className="cg-rotate-btn"
                  onClick={rotateCW}
                  title="Rotate 90°"
                >
                  🔄 Rotate
                </button>
                <button
                  className={`cg-vote-btn toggle ${lightboxImage.vote === "selected" ? "active" : ""}`}
                  onClick={() =>
                    handleVote(lightboxImage.id, lightboxImage.vote === "selected" ? "unselected" : "selected")
                  }
                >
                  {lightboxImage.vote === "selected" ? "✅ Selected" : "Select"}
                </button>
              </div>
            </div>
          </div>

          {/* Next Arrow */}
          <button
            className="cg-lightbox-arrow cg-lightbox-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

export default ClientGallery;
